// @ts-nocheck
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Edit3, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { CloneEventDialog } from "@/components/admin-dashboard/CloneEventDialog";
import AIEventBuilderChat from "@/components/admin-dashboard/AIEventBuilderChat";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";
import { supabase } from "@/integrations/supabase/client";

export default function RLeventsCreateEventPage() {
  const navigate = useNavigate();
  const { createOrgId } = useAdminOrgScope();
  // If an AI conversation is already in progress (saved draft), resume the chat panel.
  const hasAIDraft = (() => {
    try {
      return !!sessionStorage.getItem("ai-event-builder-draft");
    } catch {
      return false;
    }
  })();
  const [selectedPath, setSelectedPath] = useState<"ai" | "clone" | "scratch" | null>(
    hasAIDraft ? "ai" : null
  );
  const [cloneOpen, setCloneOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  // Resolve a single AI venue ({ name, venue_library_id?, city?, state? })
  // into a real venues row for this org and return its info.
  const resolveVenue = async (v: any) => {
    if (!v) return null;

    if (v.venue_library_id) {
      const { data: lib } = await supabase
        .from("venue_library")
        .select("name, address, city, state, zip_code, latitude, longitude, capacity")
        .eq("id", v.venue_library_id)
        .maybeSingle();

      if (lib) {
        const { data: inserted, error: insertErr } = await supabase
          .from("venues")
          .insert({
            organization_id: createOrgId,
            name: lib.name,
            address: lib.address,
            city: lib.city,
            state: lib.state,
            zip_code: lib.zip_code,
            latitude: lib.latitude,
            longitude: lib.longitude,
            capacity: lib.capacity,
            is_active: true,
          })
          .select("id, name, city, state")
          .single();
        if (insertErr) throw insertErr;
        return {
          id: inserted?.id ?? null,
          name: inserted?.name ?? lib.name,
          city: inserted?.city ?? lib.city,
          state: inserted?.state ?? lib.state,
        };
      }
    }

    // No library reference — create a new venue from the name.
    if (v.name) {
      const { data: inserted, error: insertErr } = await supabase
        .from("venues")
        .insert({
          organization_id: createOrgId,
          name: v.name,
          city: v.city ?? null,
          state: v.state ?? null,
          is_active: true,
        })
        .select("id, name, city, state")
        .single();
      if (insertErr) throw insertErr;
      return {
        id: inserted?.id ?? null,
        name: inserted?.name ?? v.name,
        city: inserted?.city ?? v.city ?? null,
        state: inserted?.state ?? v.state ?? null,
      };
    }

    return null;
  };

  // Copy AI venues into this org, then prefill + open the wizard.
  const handleAIComplete = async (event: any) => {
    setFinalizing(true);
    try {
      // Normalize: event.venues is now an array (primary venue first).
      const aiVenues: any[] = Array.isArray(event?.venues)
        ? event.venues
        : event?.venue_library_id || event?.venue_name
          ? [
              {
                name: event?.venue_name,
                venue_library_id: event?.venue_library_id,
                city: event?.city,
                state: event?.state,
              },
            ]
          : [];

      const resolved = [];
      for (const v of aiVenues) {
        const r = await resolveVenue(v);
        if (r) resolved.push(r);
      }

      const primary = resolved[0] ?? null;
      const additional = resolved.slice(1).map((r) => r.id).filter(Boolean);

      const aiPrefill = {
        name: event?.name ?? "",
        sport: event?.sport ?? "",
        gender: event?.gender ?? "",
        start_date: event?.start_date ?? "",
        end_date: event?.end_date ?? "",
        venue_id: primary?.id ?? null,
        venue_name: primary?.name ?? null,
        venue_city: primary?.city ?? null,
        venue_state: primary?.state ?? null,
        additional_venue_ids: additional,
        divisions: event?.divisions ?? [],
        base_price: event?.base_price ?? null,
        entry_price: event?.entry_price ?? null,
        team_cap: event?.team_cap ?? null,
      };

      // Persist to sessionStorage so the prefill survives the editor's
      // post-create navigation (which uses { replace: true } and drops
      // location.state) and any race with reference-data loading.
      try {
        sessionStorage.setItem("rle_ai_prefill", JSON.stringify(aiPrefill));
      } catch {
        // ignore storage errors
      }

      navigate("/admin-dashboard/events/new/edit", {
        state: { aiPrefill },
      });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to set up the event. Please try again.");
      setFinalizing(false);
    }
  };

  const handleClone = () => {
    setCloneOpen(true);
  };

  const handleBuildFromScratch = () => {
    navigate("/admin-dashboard/events/new/edit");
  };

  return (
    <div className="min-h-screen bg-background px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Create Event</h1>
          <p className="text-muted-foreground mt-2">
            Choose how you'd like to create your new tournament event
          </p>
        </div>

        {selectedPath === "ai" ? (
          <div className="rounded-2xl border-2 border-primary bg-white shadow-lg p-6 mb-6">
            <AIEventBuilderChat onComplete={handleAIComplete} />
            {finalizing && (
              <p className="text-sm text-muted-foreground mt-3">
                Setting up your event…
              </p>
            )}
            <button
              onClick={() => setSelectedPath(null)}
              className="mt-4 text-primary hover:underline text-sm font-medium"
            >
              ← Back to options
            </button>
          </div>
        ) : null}

        <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 ${selectedPath === "ai" ? "hidden" : ""}`}>
          {/* 1. AI-Assisted */}
          <div className="relative rounded-2xl border-2 border-border bg-white hover:border-primary/50 transition-all">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full">
                RECOMMENDED
              </span>
            </div>
            <div className="p-6 h-full flex flex-col">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-bold text-foreground">Describe in Plain English</h3>
                  </div>
                  <span className="inline-block bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1 rounded-full">
                    AI-assisted
                  </span>
                </div>
              </div>
              <div className="flex-1 flex flex-col justify-between">
                <p className="text-sm text-muted-foreground">
                  Chat with our AI assistant — it asks a few quick questions and builds your event for you.
                </p>
                <button
                  onClick={() => setSelectedPath("ai")}
                  className="mt-4 w-full bg-primary text-white font-semibold py-2.5 rounded-lg hover:opacity-90 transition-opacity"
                >
                  Try AI-Assisted
                </button>
              </div>
            </div>
          </div>


          {/* 2. Clone */}
          <div
            className={`rounded-2xl border-2 transition-all ${
              selectedPath === "clone"
                ? "border-primary bg-primary/5 shadow-lg"
                : "border-border bg-white hover:border-primary/50"
            }`}
          >
            <div className="p-6 h-full flex flex-col">
              <div className="flex items-start gap-2 mb-4">
                <Copy className="w-5 h-5 text-foreground flex-shrink-0 mt-1" />
                <h3 className="text-lg font-bold text-foreground">Clone from Previous</h3>
              </div>
              <div className="flex-1 flex flex-col justify-between">
                <p className="text-sm text-muted-foreground">
                  Quickly create a new event by cloning settings from a previous tournament.
                </p>
                <button
                  onClick={handleClone}
                  className="mt-4 w-full bg-secondary text-foreground font-semibold py-2.5 rounded-lg hover:bg-muted transition-colors"
                >
                  Clone Event
                </button>
              </div>
            </div>
          </div>

          {/* 3. Build from Scratch */}
          <div
            className={`rounded-2xl border-2 transition-all ${
              selectedPath === "scratch"
                ? "border-primary bg-primary/5 shadow-lg"
                : "border-border bg-white hover:border-primary/50"
            }`}
          >
            <div className="p-6 h-full flex flex-col">
              <div className="flex items-start gap-2 mb-4">
                <Edit3 className="w-5 h-5 text-foreground flex-shrink-0 mt-1" />
                <h3 className="text-lg font-bold text-foreground">Build from Scratch</h3>
              </div>
              <div className="flex-1 flex flex-col justify-between">
                <p className="text-sm text-muted-foreground">
                  Start with an empty form and fill in all the details from scratch.
                </p>
                <button
                  onClick={handleBuildFromScratch}
                  className="mt-4 w-full bg-secondary text-foreground font-semibold py-2.5 rounded-lg hover:bg-muted transition-colors"
                >
                  Start Blank
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={() => navigate("/admin-dashboard/events")}
            className="text-primary hover:underline text-sm font-medium"
          >
            ← Back to Events
          </button>
        </div>
      </div>

      <CloneEventDialog open={cloneOpen} onOpenChange={setCloneOpen} />
    </div>
  );
}
